import { Option, Result } from '@sapphire/result';
import {
  ADVANCED_FILTER_TYPE,
  AdvancedFeasibilityFilters,
  SurveyKind,
  MC_MESSAGES,
  Survey,
  UniqueLinksForm,
} from '@ps/survey-utils';

export interface ChildSwitchContext {
  readonly advancedFilters: AdvancedFeasibilityFilters;
  readonly isRespondentStatusFilterEnabled: boolean;
  readonly isManualProcessingEnabled: boolean;
  readonly isFeasibilityLoading: boolean;
  readonly isUniqueLinksEnabled: boolean;
  readonly uniqueLinksCount: number;
}

export type ChildSwitchContextSources = Omit<
  ChildSwitchContext,
  'isUniqueLinksEnabled' | 'uniqueLinksCount'
> & {
  readonly uniqueLinks: UniqueLinksForm;
};

export function buildChildSwitchContext({
  advancedFilters,
  isRespondentStatusFilterEnabled,
  isManualProcessingEnabled,
  isFeasibilityLoading,
  uniqueLinks,
}: ChildSwitchContextSources): ChildSwitchContext {
  return {
    advancedFilters,
    isRespondentStatusFilterEnabled,
    isManualProcessingEnabled,
    isFeasibilityLoading,
    isUniqueLinksEnabled: uniqueLinks.uniqueLinksEnabled,
    uniqueLinksCount: uniqueLinks.entryLinks.length,
  };
}

export interface ChildSwitchViolation {
  readonly message: string;
  readonly severity: 'error' | 'warning';
}

export type ChildSwitchValidation = (
  context: ChildSwitchContext,
) => Result<void, ChildSwitchViolation>;

const manualProcessingSettled: ChildSwitchValidation = ({
  isManualProcessingEnabled,
  isFeasibilityLoading,
}) =>
  isManualProcessingEnabled && isFeasibilityLoading
    ? Result.err({
        message: MC_MESSAGES.MANUAL_PROCESSING_IN_FLIGHT,
        severity: 'warning',
      })
    : Result.ok();

const respondentStatusSelected: ChildSwitchValidation = ({
  advancedFilters,
  isRespondentStatusFilterEnabled,
}) =>
  isRespondentStatusFilterEnabled &&
  advancedFilters.type === ADVANCED_FILTER_TYPE.EXCLUDE &&
  !advancedFilters.respondentStatuses.length
    ? Result.err({
        message: MC_MESSAGES.NO_RESPONDENT_STATUS_SELECTED,
        severity: 'error',
      })
    : Result.ok();

const uniqueLinksUploaded: ChildSwitchValidation = ({
  isUniqueLinksEnabled,
  uniqueLinksCount,
}) =>
  isUniqueLinksEnabled && !uniqueLinksCount
    ? Result.err({
        message: MC_MESSAGES.UNIQUE_LINKS_NOT_UPLOADED,
        severity: 'error',
      })
    : Result.ok();

const MC_TRADITIONAL_VALIDATIONS: readonly ChildSwitchValidation[] = [
  manualProcessingSettled,
  respondentStatusSelected,
  uniqueLinksUploaded,
];
const MC_TRADITIONAL_ADD_VALIDATIONS: readonly ChildSwitchValidation[] = [
  manualProcessingSettled,
];
const NO_VALIDATIONS: readonly ChildSwitchValidation[] = [];

export function validateChildSwitch(
  survey: Option<Survey>,
  context: ChildSwitchContext,
): Result<void, ChildSwitchViolation> {
  return survey.match({
    some: (openSurvey) =>
      runValidations(
        validationsFor(openSurvey, MC_TRADITIONAL_VALIDATIONS),
        context,
      ),
    none: () => Result.ok(),
  });
}

export function validateLocaleAdd(
  survey: Option<Survey>,
  context: ChildSwitchContext,
): Result<void, ChildSwitchViolation> {
  return survey.match({
    some: (openSurvey) =>
      runValidations(
        validationsFor(openSurvey, MC_TRADITIONAL_ADD_VALIDATIONS),
        context,
      ),
    none: () => Result.ok(),
  });
}

function runValidations(
  validations: readonly ChildSwitchValidation[],
  context: ChildSwitchContext,
): Result<void, ChildSwitchViolation> {
  return validations.reduce<Result<void, ChildSwitchViolation>>(
    (outcome, validate) => (outcome.isErr() ? outcome : validate(context)),
    Result.ok(),
  );
}

function validationsFor(
  survey: Survey,
  validations: readonly ChildSwitchValidation[],
): readonly ChildSwitchValidation[] {
  return SurveyKind.isTraditionalMc(survey) ? validations : NO_VALIDATIONS;
}
